import React, { useRef, useState, useEffect, FC } from 'react'
import { useIsFocused } from '@react-navigation/native'
import { View, Dimensions } from 'react-native'


export interface IDimensionData {
  rectTop: number;
  rectBottom: number;
  rectWidth: number;
}

export interface VisibilityProps {
  /** Function that is triggered when component enters the viewport */
  onChange?(visible: boolean): void;
  /** The component that needs to be in the viewport */
  children: React.FC<boolean> | React.ReactElement;
}
const VisibilitySensor: FC<VisibilityProps> = ({
  children,
  onChange,
}: VisibilityProps) => {
  const myView: React.RefObject<View> = useRef(null)
  const [lastValue, setLastValue] = useState<boolean>(false)
  const lastValueRef = useRef<boolean>(false)
  const intervalRef = useRef(null)

  const isFocused = useIsFocused()

  useEffect(() => {
    const checkVisibility = () => {
      if (!myView || !myView.current) return
      if (!isFocused) {
        if (lastValueRef.current !== false) {
          lastValueRef.current = false
          setLastValue(false)
          onChange?.(false)
        }
        return
      }

      myView.current.measure(
        (_x: number, _y: number, width: number, height: number, pageX: number, pageY: number) => {
          const windowDims = Dimensions.get('window')
          const rectTop = pageY
          const rectBottom = pageY + height
          const rectLeft = pageX
          const rectRight = pageX + width

          const overlapWidth = Math.min(rectRight, windowDims.width) - Math.max(rectLeft, 0)
          const overlapHeight = Math.min(rectBottom, windowDims.height) - Math.max(rectTop, 0)

          const visibleWidth = Math.max(0, overlapWidth)
          const visibleHeight = Math.max(0, overlapHeight)

          const visibleArea = visibleWidth * visibleHeight
          const totalArea = width * height

          const visibilityRatio = totalArea > 0 ? visibleArea / totalArea : 0
          const isVisible = visibilityRatio >= 0.2

          if (lastValueRef.current !== isVisible) {
            lastValueRef.current = isVisible
            setLastValue(isVisible)
            onChange?.(isVisible)
          }
        }
      )
    }

    // initial measure
    checkVisibility()
    // watch on interval without causing rerenders unless visibility toggles
    if (!intervalRef.current) {
      intervalRef.current = setInterval(checkVisibility, 250)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [onChange, isFocused])

  return (
    <View collapsable={false} ref={myView}>
      {typeof children === 'function' ? children(lastValue) : children}
    </View>
  )
}

export default VisibilitySensor